import * as React from "react";
import { Dispatch, connect } from "react-redux";
import * as moment from "moment";
import * as qs from 'query-string';
import * as _ from "lodash";
import { Table } from "react-bootstrap";
import { toast } from "react-toastify";
import {
    IBaseResponse, IPosition,
    ILogistics, ILogisticsResponse, ILogisticsRequest, IPairStatusChangeRequest, IClusterListResponse, IRecipientResponse, ILogisticsOptionRequest, ILogisticsWithOption, IUser, ICluster, IListItem, ICenter, ILogisticsProvider, ITransportType, IDonor, IRecipient, ISoleSourceDonor
} from "@nkr/models/index";
import { ITransplantDetails } from "../../models";
import { ChainStatus, ClusterStatus, EmailStatusType, LogisticsAudience, TransportMethod } from "@nkr/enums/index";
import {
    AppActionCreators, SwapActionCreators, IApplicationState, ISwapState, SwapActionTypes, IToggleGpsResponse,
    ILogisticsListResponse, ISavePositionDateTimeResponse, ILogisticsOptionResponse, IMatchingSurgeriesAndRedeyeResponse, CenterActionCreators,
    ContactActionCreators
} from "@nkr/store/index";
import { swapTypeMap } from './swap-type-map';
import PositionListRow from "./position-list-row";
import { HeaderRowWrapper } from "./styles";
import Logistics from "../logistics";
import ResetConfirmation from "./modals/reset-confirmation";
import TransplantDetails from "./modals/transplant-details";
import DonorStatus from "./modals/donor-status";
import OverrideAnatomyPreference from "./modals/override-anatomy-preference";
import LogisticsOption from "../logistics/option";
import PairStatusChangeBlocked from './modals/pair-status-change-blocked';
import TriggerRexmEmail from './modals/trigger-rexm-email';
import { downloadFile } from '@nkr/services/download-file';
import RecipientOverlay from "@nkr/views/matching/results-new/recipient-overlay";

interface IOwnProps {
    chainId: number,
    clusterNumber: number,
    clusterId: number,
    chainStatusId: ChainStatus,
    clusterStatusId: ClusterStatus,
    swapChainStatusId: ChainStatus,
    swapClusterStatusId: ClusterStatus,
    user: IUser,
    showMap: (url: string) => void,
    onUpdateAfterReset: () => void,
    onUpdateSwaps: (message: string, success: boolean, refresh: boolean) => void
}

interface IStateProps {
    swaps: ISwapState
}

interface IDispatchProps {
    showLoading: (message: string) => Promise<void>,
    hideLoading: () => void,
    getLogisticsData: (donorId: number, recipientId: number) => Promise<ILogisticsResponse>,
    getLogisticsDataByChainCluster: (chainId: number, clusterNumber: number) => Promise<ILogisticsListResponse>,
    getLogisticsDataByChainClusterPosition: (chainId: number, clusterNumber: number, positionNumber: number) => Promise<ILogisticsListResponse>,
    saveLogisticsData: (logistics: ILogisticsRequest) => Promise<IBaseResponse>,
    resetChain: (recipientId: number) => Promise<IRecipientResponse>,
    savePairStatus: (request: IPairStatusChangeRequest) => Promise<IBaseResponse>,
    saveKidneyPickupDateTime: (chainId: number, clusterNumber: number, positionNumber: number, pickupDateTime: string, isLoop: boolean) => Promise<ISavePositionDateTimeResponse>,
    saveKidneyArrivalDateTime: (chainId: number, clusterNumber: number, positionNumber: number, arrivalDateTime: string, isLoop: boolean) => Promise<ISavePositionDateTimeResponse>,
    saveMatchOfferDeadlineDateTime: (chainId: number, clusterNumber: number, positionNumber: number, matchOfferDeadlineDateTime: string, isLoop: boolean) => Promise<ISavePositionDateTimeResponse>,
    saveDonorClearanceDeadlineDateTime: (chainId: number, clusterNumber: number, positionNumber: number, crossMatchDeadlineDateTime: string, isLoop: boolean) => Promise<ISavePositionDateTimeResponse>,
    createPlaceholder: (centerId: number, donorId: number) => Promise<IBaseResponse>,
    updateRexm: (donorId: number, recipientId: number) => Promise<IBaseResponse>,
    updateGps: (clusterId: number, positionNumber: number, toggle: boolean) => Promise<IToggleGpsResponse>,
    getSwaps: (chainStatus: number, clusterStatus: number) => Promise<IClusterListResponse>,
    getLogisticsOption: (chainId: number, clusterNumber: number, positionNumber: number) => Promise<ILogisticsOptionResponse>,
    saveLogisticsOption: (request: ILogisticsOptionRequest) => Promise<ILogisticsOptionResponse>,
    getMatchingSurgeriesAndRedeye: (dateValue: string, centerId: number, edcludeId: number, isRecipient: boolean, chainId: number, clusterNumber: number, positionNumber: number) => Promise<IMatchingSurgeriesAndRedeyeResponse>,
    getCenterAddressesDownload: (donorCenterId: number, recipCenterId: number, clusterId: number) => Promise<any>,
    getCenterContactsDownload: (clusterId: number) => Promise<any>,
    deleteLogisticsOptions: (chainId: number, clusterNumber: number, positionMumber: number) => Promise<IBaseResponse>,
    returnLogisticsOptions: (chainId: number, clusterNumber: number, positionMumber: number) => Promise<IBaseResponse>
}

interface IPlaceholder {
    centerId: number,
    donorId: number
}

type TProps =
    IStateProps
    & IDispatchProps
    & IOwnProps;

interface IState {
    logistics: {
        show: boolean,
        list: ILogistics[],
        data: ILogistics | null
    },
    reset: {
        showConfirmation: boolean,
        recipientId: number
    },
    transplantDetails: {
        showTransplantDetails: boolean,
        data: ITransplantDetails
    },
    pairStatusDetails: {
        show: boolean,
        positionNumber: number,
        donorStatus: number
    },
    triggerRexm: {
        show: boolean,
        donorId: number,
        recipientId: number
    },
    placeholders: IPlaceholder[],
    overrideAnatomyPreference: {
        show: boolean,
        centerId: number,
        centerAlias: string,
        donorId: number
    },
    plans: {
        show: boolean,
        data: ILogisticsWithOption
    },
    markTransplantedBlocked: {
        show: boolean,
        data: IPosition
    },
    showPlaceholder: boolean,
    queryStatus: number,
    overlay: {
        show: boolean,
        donor: IDonor | null,
        recipient: IRecipient | null,
        intendedDonors: IDonor[],
        donors: ISoleSourceDonor[],
        dataHtml: string,
        rowRef: HTMLTableRowElement | null
    },
}

const initialState: IState = {
    logistics: {
        show: false,
        list: [],
        data: null,
    },
    reset: {
        showConfirmation: false,
        recipientId: 0
    },
    transplantDetails: {
        showTransplantDetails: false,
        data: {
            positionNumber: 0,
            pairStatus: 0,
            donorStatus: 0,
            transplantDate: "",
            transportMethod: 0,
            swapType: 0,
            transportCost: 0,
            logisticsProvider: 0,
            donorCenterAlias: "",
            donorCenterId: 0,
            rdnCenterAlias: "",
            rdnCenterId: 0,
            recipientCenterAlias: "",
            recipientCenterId: 0
        }
    },
    pairStatusDetails: {
        show: false,
        positionNumber: 0,
        donorStatus: 0
    },
    triggerRexm: {
        show: false,
        donorId: 0,
        recipientId: 0
    },
    placeholders: [],
    overrideAnatomyPreference: {
        show: false,
        centerId: 0,
        centerAlias: '',
        donorId: 0
    },
    plans: {
        show: false,
        data: null
    },
    markTransplantedBlocked: {
        show: false,
        data: null
    },
    showPlaceholder: false,
    queryStatus: -1,
    overlay: {
        show: false,
        donor: null,
        recipient: null,
        intendedDonors: [],
        donors: [],
        dataHtml: '',
        rowRef: null
    }
};

class PositionList extends React.Component<TProps, IState> {
    readonly state: IState = initialState;

    tableRef = React.createRef<HTMLTableElement>();

    componentDidMount = () => {

        let parsed = qs.parse(window.location.search)
        let queryStatus = parsed.status ? parsed.status : -1;

        let placeholders = this.getPositions().map(position => { return { centerId: 0, donorId: position.donor.id } });

        this.setState({
            placeholders,
            queryStatus
        });
    }

    handleEditLogistics = (positionNumber: number) => {
        let positions = this.getPositions();
        let position = positions.find(p => p.positionNumber == positionNumber);

        this.props.showLoading("Fetching logistics information, one moment please...").then(() => {
            this.props.getLogisticsDataByChainClusterPosition(position.chainId, position.clusterNumber, position.positionNumber).then((response: ILogisticsListResponse) => {
                let { success, results } = response.data;

                if (success) {
                    let data = results[0];

                    this.setState({
                        logistics: {
                            show: true,
                            list: results,
                            data
                        }
                    }, () => {
                        this.props.hideLoading();
                    });
                }
            });
        });
    }

    handleSaveLogistics = (logistics: ILogisticsRequest) => {
        this.props.showLoading("Saving logistics information, one moment please...").then(() => {
            this.props.saveLogisticsData(logistics).then((response: IBaseResponse) => {
                this.props.hideLoading();

                let { success } = response.data;

                if (success) {
                    this.setState({
                        logistics: {
                            show: false,
                            list: [],
                            data: null
                        }
                    }, () => {
                        this.props.onUpdateSwaps('Logistics saved successfully!', true, true);
                    });
                }
            });
        });
    }

    handleCancelLogistics = () => {
        this.setState({
            logistics: {
                show: false,
                list: [],
                data: null
            }
        });
    }

    handleSwitchToCustomLogistics = (positionNumber: number) => {
        this.handleCancelLogistics();
        this.handleEditCustomLogistics(positionNumber);
    }

    handleEditCustomLogistics = (positionNumber: number) => {
        var positions = this.getPositions();
        var position = positions.find(p => p.positionNumber == positionNumber);

        this.props.showLoading("Fetching swap logistic plans, one moment please...").then(() => {
            this.props.getLogisticsOption(position.chainId, position.clusterNumber, positionNumber).then((response: ILogisticsOptionResponse) => {
                let { success, results } = response.data;

                if (success) {
                    this.setState({
                        plans: {
                            show: true,
                            data: results
                        }
                    }, () => {
                        this.props.hideLoading();
                    });
                }
            });
        });
    }

    handleSaveOptions = (request: ILogisticsOptionRequest) => {
        this.props.showLoading("Saving logistics options, one moment please...").then(() => {
            this.props.saveLogisticsOption(request).then((response: ILogisticsOptionResponse) => {
                this.props.hideLoading();

                let { success } = response.data;

                if (success) {
                    this.setState({
                        plans: {
                            show: false,
                            data: null,
                        }
                    }, () => {
                        toast.success("Plans saved successfully!", {
                            position: toast.POSITION.TOP_CENTER,
                            autoClose: 3000
                        });
                    });
                }
            });
        });
    }

    handleDeleteOptions = (chainId: number, clusterNumber: number, positionNumber: number) => {
        this.props.showLoading('Deleting options, one moment please...').then(() => {
            this.props.deleteLogisticsOptions(chainId, clusterNumber, positionNumber).then(response => {
                this.props.hideLoading();

                if (response.data.success) {
                    this.setState({
                        plans: {
                            show: false,
                            data: null,
                        }
                    }, () => {
                        toast.success("Options deleted successfully!", {
                            position: toast.POSITION.TOP_CENTER,
                            autoClose: 3000
                        });
                    });
                }
            });
        });
    }

    handleReturnOptions = (chainId: number, clusterNumber: number, positionNumber: number) => {
        this.props.showLoading('Return options to courier, one moment please...').then(() => {
            this.props.returnLogisticsOptions(chainId, clusterNumber, positionNumber).then(response => {
                this.props.hideLoading();

                if (response.data.success) {
                    this.setState({
                        plans: {
                            show: false,
                            data: null,
                        }
                    }, () => {
                        toast.success("Options return to courier successfully!", {
                            position: toast.POSITION.TOP_CENTER,
                            autoClose: 3000
                        });
                    });
                }
            });
        });
    }

    handleCancelOptions = () => {
        this.setState({
            plans: {
                show: false,
                data: null
            }
        });
    }

    handleSwitchToSwapLogistics = (positionNumber: number) => {
        this.handleCancelOptions();
        this.handleEditLogistics(positionNumber);
    }

    handleShowMap = (url: string) => {
        this.props.showMap(url);
    }

    handleKidneyPickupChange = (positionNumber: number, kidneyPickupDateTime: string) => {
        let position = this.getPositions().find(x => x.positionNumber == positionNumber);
        let { chainId, clusterNumber, donor } = position;
        let { id: excludeId, center, rdnCenterId } = donor;
        let cluster = this.props.swaps.list.find(x => x.id == this.props.clusterId);
        let isLoop = cluster.donationType == 'LOOP';
        let donorCenterId = rdnCenterId > 0 ? rdnCenterId : center.id;

        this.props.showLoading("Saving kidney pickup date/time, one moment please...").then(() => {
            Promise.all([
                this.props.saveKidneyPickupDateTime(this.props.chainId, this.props.clusterNumber, positionNumber, kidneyPickupDateTime, isLoop),
                this.props.getMatchingSurgeriesAndRedeye(kidneyPickupDateTime, donorCenterId, excludeId, false, chainId, clusterNumber, positionNumber)
            ])
                .then(([responseSave, responseMatchingSurgeries]) => {
                    if (responseSave.data.success == false && responseSave.data.message != "") {
                        this.props.onUpdateSwaps(responseSave.data.message, false, false);
                    }
                    else if (responseSave.data.success && responseMatchingSurgeries.data.success) {
                        this.props.onUpdateSwaps("Kidney pickup date/time updated!", true, true);
                    } else {
                        this.props.onUpdateSwaps("Unable to record kidney pickup date/time...", false, false);
                    }
                });
        });
    }

    handleKidneyDeliveryChange = (positionNumber: number, kidneyArrivalDateTime: string) => {
        let position = this.getPositions().find(x => x.positionNumber == positionNumber);
        let { chainId, clusterNumber, recipient } = position;
        let { id: excludeId, center } = recipient;
        let cluster = this.props.swaps.list.find(x => x.id == this.props.clusterId);
        let isLoop = cluster.donationType == 'LOOP';

        this.props.showLoading("Saving kidney delivery date/time, one moment please...").then(() => {
            Promise.all([
                this.props.saveKidneyArrivalDateTime(this.props.chainId, this.props.clusterNumber, positionNumber, kidneyArrivalDateTime, isLoop),
                this.props.getMatchingSurgeriesAndRedeye(kidneyArrivalDateTime, center.id, excludeId, true, chainId, clusterNumber, positionNumber)
            ])
                .then(([responseSave, responseMatchingSurgeries]) => {

                    if (responseSave.data.success == false && responseSave.data.message != "") {
                        this.props.onUpdateSwaps(responseSave.data.message, false, false);
                    }
                    else if (responseSave.data.success && responseMatchingSurgeries.data.success) {
                        this.props.onUpdateSwaps("Kidney arrival date/time updated!", true, true);
                    } else {
                        this.props.onUpdateSwaps("Unable to record kidney arrival date/time...", false, false);
                    }
                });
        });
    }

    handleMatchOfferDeadlineChange = (positionNumber: number, matchOfferDeadlineDateTime: string) => {
        let cluster = this.props.swaps.list.find(x => x.id == this.props.clusterId);
        let isLoop = cluster.donationType == 'LOOP';

        this.props.showLoading("Saving match offer deadline date/time, one moment please...").then(() => {
            this.props.saveMatchOfferDeadlineDateTime(this.props.chainId, this.props.clusterNumber, positionNumber, matchOfferDeadlineDateTime, isLoop).then((response: ISavePositionDateTimeResponse) => {
                if (response.data.success)
                    this.props.onUpdateSwaps("Match offer deadline date/time updated!", true, false);
                else
                    this.props.onUpdateSwaps("Unable to record match offer deadline date/time...", false, false);
            });
        });
    }

    handleCrossMatchDeadlineChange = (positionNumber: number, crossMatchDeadlineDateTime: string) => {
        let cluster = this.props.swaps.list.find(x => x.id == this.props.clusterId);
        let isLoop = cluster.donationType == 'LOOP';

        this.props.showLoading("Saving donor clearance deadline date/time, one moment please...").then(() => {
            this.props.saveDonorClearanceDeadlineDateTime(this.props.chainId, this.props.clusterNumber, positionNumber, crossMatchDeadlineDateTime, isLoop).then((response: ISavePositionDateTimeResponse) => {
                if (response.data.success)
                    this.props.onUpdateSwaps("Donor Clearance deadline date/time updated!", true, false);
                else
                    this.props.onUpdateSwaps("Unable to record donor clearance deadline date/time...", false, false);
            });
        });
    }

    handleCreatePlaceholder = (centerId: number, centerAlias: string, donorId: number, hasAcceptableAnatomy: boolean) => {
        if (hasAcceptableAnatomy) {
            this.createPlaceholder(centerId, donorId);
        } else {
            let placeholders = this.state.placeholders.map(ph => {
                if (ph.donorId == donorId) {
                    ph.centerId = centerId;
                }

                return ph;
            });

            this.setState({
                placeholders,
                overrideAnatomyPreference: {
                    show: true,
                    centerId,
                    centerAlias,
                    donorId
                }
            });
        }
    }

    handleUpdateGps = (clusterId: number, positionNumber: number, toggle: boolean) => {
        this.props.updateGps(clusterId, positionNumber, toggle);
    }

    // reset chain

    handleResetChain = (recipientId: number) => {
        var reset = {
            showConfirmation: true,
            recipientId
        };

        this.setState({
            reset
        });
    }

    handleResetYes = () => {
        let reset = { ...this.state.reset, showConfirmation: false };

        this.setState({
            reset
        }, () => {
            this.props.showLoading("Attempting to reset the chain, one moment please...").then(() => {
                this.props.resetChain(this.state.reset.recipientId).then((response: IRecipientResponse) => {
                    if (response.data.success) {
                        this.props.onUpdateAfterReset();
                    }

                    this.props.hideLoading();
                });
            });
        });
    }

    handleResetNo = () => {
        let reset = {
            showConfirmation: false,
            recipientId: 0
        };

        this.setState({
            reset
        });
    }

    // transplant details/pair status

    handlePairStatusChange = (positionNumber: number, pairStatusId: number) => {
        let swap = this.getSwap();
        let positions = this.getPositions();
        let position = positions.find(p => p.positionNumber == positionNumber);
        let currentPairStatus = this.props.swaps.pairStatusList.find(pairStatus => pairStatus.id == position.pairStatusId);
        let newPairStatus = this.props.swaps.pairStatusList.find(pairStatus => pairStatus.id == pairStatusId);

        let transportMethodActualId = 0;
        let transportCostActual = '';

        if (position.donor.logistics != null) {
            transportMethodActualId = position.donor.logistics.transportMethodActualId;
            transportCostActual = position.donor.logistics.transportCostActual;
        }

        let isCharter =
            transportMethodActualId == TransportMethod.Charter
            || transportMethodActualId == TransportMethod.CharterComm
            || transportMethodActualId == TransportMethod.CommCharter;

        if (currentPairStatus != null && newPairStatus != null) {
            if (newPairStatus.description.toUpperCase() == "TRANSPLANTED") {
                if (isCharter && transportCostActual == '') {
                    this.setState({
                        markTransplantedBlocked: {
                            show: true,
                            data: position
                        }
                    });
                } else {
                    this.props.showLoading("Fetching logistics information, one moment please...").then(() => {
                        this.props.getLogisticsData(position.donor.id, position.recipient.id).then((response: ILogisticsResponse) => {
                            let { success, results } = response.data;

                            if (success) {
                                let { donorCenter: donorCenterAlias, donorCenterId, rdnCenterAlias, rdnCenterId, recipientCenter: recipientCenterAlias, recipientCenterId } = results;

                                this.setState({
                                    transplantDetails: {
                                        showTransplantDetails: true,
                                        data: {
                                            positionNumber,
                                            pairStatus: pairStatusId,
                                            donorStatus: 0,
                                            transplantDate: results.kidneyArrivalDateTime,
                                            transportMethod: results.transportMethodActual != "" ? parseInt(results.transportMethodActual) : 0,
                                            transportCost: results.transportCostActual != '' ? parseInt(results.transportCostActual) : 0,
                                            swapType: swapTypeMap[swap.swapType],
                                            logisticsProvider: results.logisticsProvider != '' ? parseInt(results.logisticsProvider) : 0,
                                            donorCenterAlias,
                                            donorCenterId,
                                            rdnCenterAlias,
                                            rdnCenterId,
                                            recipientCenterAlias,
                                            recipientCenterId
                                        }
                                    }
                                }, () => {
                                    this.props.hideLoading();
                                });
                            } else {
                                this.props.hideLoading();
                                this.props.onUpdateSwaps((response.data as any).message || "Unable to fetch logistics information", false, false);
                            }
                        });
                    });
                }
            }
            else if (newPairStatus.description.toUpperCase() != "TRANSPLANTED" && currentPairStatus.description.toUpperCase() == "TRANSPLANTED") {
                this.setState({
                    pairStatusDetails: {
                        show: true,
                        positionNumber,
                        donorStatus: pairStatusId
                    }
                });
            }
            else {
                let request: IPairStatusChangeRequest = {
                    chainId: position.chainId,
                    clusterNumber: position.clusterNumber,
                    positionNumber,
                    pairStatus: pairStatusId,
                    transplantDate: null,
                    shippedMethod: null,
                    swapType: null,
                    transportationCost: null,
                    logisticsProvider: null
                };

                this.updatePairStatus(request, "Updating pair status...", "Pair status updated", "Pair status could not be updated");
            }
        }
    }

    handleUpdateTransplantDetailsSave = (transplantDate: string, logisticsProvider: number, transportMethod: number, transportCost: number, swapType: number) => {
        let data = { ...this.state.transplantDetails.data, transplantDate, logisticsProvider, transportMethod, transportCost, swapType };

        this.setState({
            transplantDetails: { data, showTransplantDetails: false }
        }, () => {
            let positions = this.getPositions();

            let { positionNumber, pairStatus, transplantDate, transportMethod, transportCost, logisticsProvider } = this.state.transplantDetails.data;
            var { chainId, clusterNumber, recipient } = positions.find(p => p.positionNumber == positionNumber);

            let request: IPairStatusChangeRequest = {
                chainId,
                clusterNumber,
                positionNumber,
                pairStatus,
                transplantDate: (transplantDate != "" ? moment(transplantDate).format() : ""),
                shippedMethod: transportMethod,
                swapType: swapType,
                transportationCost: transportCost,
                logisticsProvider
            };

            this.updatePairStatus(request, "Updating transplant details, one moment please...", "Transplant details updated", "Transplant details could not be updated");
        });
    }

    handleUpdateTransplantDetailsCancel = () => {
        this.setState({
            transplantDetails: { ...this.state.transplantDetails, showTransplantDetails: false }
        });
    }

    // donor status

    handleDonorStatusChange = (donorStatus: number) => {
        this.setState({
            pairStatusDetails: { ...this.state.pairStatusDetails, donorStatus }
        });
    }

    handleUpdateDonorStatusSave = () => {
        this.setState({
            pairStatusDetails: { ...this.state.pairStatusDetails, show: false }
        }, () => {
            let positions = this.getPositions();

            let { positionNumber, donorStatus } = this.state.pairStatusDetails;
            let { chainId, clusterNumber } = positions.find(p => p.positionNumber == positionNumber);

            let request: IPairStatusChangeRequest = {
                chainId,
                clusterNumber,
                positionNumber,
                pairStatus: donorStatus,
                transplantDate: null,
                shippedMethod: null,
                swapType: null,
                transportationCost: null,
                logisticsProvider: null
            };

            this.updatePairStatus(request, "Updating donor status, one moment please...", "Donor status updated!", "Donor status could not be updated");
        });
    }

    handleUpdateDonorStatusCancel = () => {
        this.setState({
            pairStatusDetails: {
                ...this.state.pairStatusDetails,
                show: false
            }
        });
    }

    // trigger rexm

    handleConfirmRexm = (donorId: number, recipientId: number) => {
        this.setState({
            triggerRexm: {
                show: true,
                donorId,
                recipientId
            }
        });
    }

    handleTriggerRexmSend = () => {
        let { donorId, recipientId } = this.state.triggerRexm;

        this.setState({
            triggerRexm: {
                show: false,
                donorId: 0,
                recipientId: 0
            }
        }, () => {
            this.props.showLoading("Attempting to update the REXM, one moment please...").then(() => {
                this.props.updateRexm(donorId, recipientId).then((response: IBaseResponse) => {
                    if (response.data.success)
                        this.props.onUpdateSwaps("REXM update successfully!", true, false);
                    else
                        this.props.onUpdateSwaps("Unable to update the REXM...", false, false);
                });
            });
        });
    }

    handleTriggerRexmCancel = () => {
        this.setState({
            triggerRexm: {
                show: false,
                donorId: 0,
                recipientId: 0
            }
        })
    }

    // override anatomy preference

    handleOverrideAnatomyPreferenceYes = () => {
        let { centerId, donorId } = this.state.overrideAnatomyPreference;

        this.setState({
            overrideAnatomyPreference: { ...this.state.overrideAnatomyPreference, show: false }
        }, () => {
            this.createPlaceholder(centerId, donorId);
        });
    }

    handleOverrideAnatomyPreferenceNo = () => {
        let placeholders = this.state.placeholders.map(ph => {
            if (ph.donorId == this.state.overrideAnatomyPreference.donorId) {
                ph.centerId = 0;
            }

            return ph;
        });

        this.setState({
            placeholders,
            overrideAnatomyPreference: { ...this.state.overrideAnatomyPreference, show: false }
        });
    }

    handleMarkTransplantedBlockedClose = () => {
        this.setState({
            markTransplantedBlocked: {
                show: false,
                data: null
            }
        });
    }

    getSwap = () => {
        let swap = this.props.swaps.list.find(swap => swap.chainId == this.props.chainId && swap.clusterNumber == this.props.clusterNumber);

        return swap;
    }

    getPositions = () => {
        let swap = this.props.swaps.list.find(swap => swap.chainId == this.props.chainId && swap.clusterNumber == this.props.clusterNumber);

        let positions = _.orderBy(swap.positions, (position: IPosition) => {
            return position.positionNumber;
        });

        return positions;
    }

    updatePairStatus = (request: IPairStatusChangeRequest, loadingMessage: string, successMessage: string, otherMessage: string) => {

        try {
            this.props.showLoading(loadingMessage).then(() => {
                this.props.savePairStatus(request).then((response: IBaseResponse) => {
                    if (response.data.success) {
                        this.props.onUpdateSwaps(successMessage, true, false);
                    } else {
                        if (response.data.message)
                            this.props.onUpdateSwaps(response.data.message, false, false);
                        else
                            this.props.onUpdateSwaps(otherMessage, false, false);
                    }
                }).catch(function () { toast.error("Error saving the status.") })
            });
        } finally {
            this.props.hideLoading();
        }
    }

    createPlaceholder = (centerId: number, donorId: number) => {
        this.props.showLoading("Creating recipient placeholder, one moment please...").then(() => {
            this.props.createPlaceholder(centerId, donorId).then(response => {
                if (response.data.success) {
                    this.props.getSwaps(this.props.chainStatusId, this.props.clusterStatusId).then(() => {
                        toast.success("Successfully created the placeholder for the recipient...");

                        this.props.hideLoading();
                    });
                }
            });
        });
    }

    handleAddressDownload = (donorCenterId: number, recipientCenterId: number, clusterId: number) => {
        this.props.showLoading("Downloading File...").then(() => {

            this.props.getCenterAddressesDownload(donorCenterId, recipientCenterId, clusterId).then((response: any) => {
                downloadFile(response.data, `logistics_address_list.rtf`, 'application/rtf');

                this.props.hideLoading();
            });
        });
    }

    handleContactsDownload = (clusterId: number) => {
        this.props.showLoading("Downloading File...").then(() => {

            this.props.getCenterContactsDownload(clusterId).then((response: any) => {
                downloadFile(response.data, `logistics_contacts_list.csv`, 'text/csv');
                this.props.hideLoading();
            });
        });
    }


    handleTogglePlaceholder = (showPlaceholder: boolean) => {
        this.setState({
            showPlaceholder
        });
    }

    getMatchingSurgeries = (dateValue: string, centerId: number, excludeId: number, isRecipient: boolean, positionNumber: number): Promise<IMatchingSurgeriesAndRedeyeResponse> => {
        let position = this.getPositions().find(x => x.positionNumber == positionNumber);
        let { chainId, clusterNumber } = position;

        return this.props.getMatchingSurgeriesAndRedeye(dateValue, centerId, excludeId, isRecipient, chainId, clusterNumber, positionNumber);
    }


    handleShowOverlay = (donor: IDonor, recipient: IRecipient, soleSourceDonors: ISoleSourceDonor[], ssdHtml: string, rowRef: HTMLTableRowElement, intendedDonors: IDonor[]) => {
        this.setState({
            overlay: {
                show: true,
                donor,
                recipient,
                donors: soleSourceDonors,
                intendedDonors,
                dataHtml: ssdHtml,
                rowRef
            }
        });
    };

    handleHideOverlay = () => {
        this.setState(prev => ({
            overlay: {
                show: false,
                donor: null,
                recipient: null,
                intendedDonors: [],
                donors: [],
                dataHtml: '',
                rowRef: prev.overlay.rowRef
            }
        }));
    };

    render = () => {
        const positions = this.getPositions();
        const blockedPosition = this.state.markTransplantedBlocked.data != null ? `${this.state.markTransplantedBlocked.data.chainId}/${this.state.markTransplantedBlocked.data.clusterNumber}/${this.state.markTransplantedBlocked.data.positionNumber}` : '';

        return (
            <>
                <div ref={this.tableRef} style={{ position: 'relative' }}>
                    <Table condensed style={{ marginBottom: 0, width: "100%", border: "1px solid #ddd", borderCollapse: "collapse" }}>
                        <thead>
                            <HeaderRowWrapper>
                                <th style={{ width: 25, textAlign: "center" }}>P</th>
                                <th style={{ width: 130 }}>D Center</th>
                                <th style={{ width: 110 }}>Donor</th>

                                {this.props.clusterStatusId == ClusterStatus.Offers && (
                                    <th style={{ width: 50 }}>CPR</th>
                                )}

                                <th style={{ width: 115 }}>R Center</th>
                                <th style={{ width: 100 }}>Recip</th>
                                <th style={{ width: 31 }}>DB</th>
                                <th style={{ width: 30 }}>RB</th>
                                <th style={{ width: 31 }}>DA</th>
                                <th style={{ width: 31 }}>RA</th>
                                <th style={{ width: 56 }}>R Type</th>
                                <th style={{ width: 62 }}>cPRA4</th>
                                <th style={{ width: 86 }}>Eplet MM</th>
                                <th style={{ width: 76 }}>Allele M</th>
                                <th style={{ width: 41 }}>PTS</th>
                                <th style={{ width: 34 }}>WT</th>
                                <th style={{ width: 135 }}>Pair Status</th>

                                {this.props.swapClusterStatusId == ClusterStatus.Logistics || this.props.swapClusterStatusId == ClusterStatus.Kickoff || this.props.swapClusterStatusId == ClusterStatus.InProcess ?
                                    <>
                                        <th style={{ width: 45 }}>GPS</th>
                                        <th style={{ width: 155 }}>KPickup</th>
                                        <th style={{ width: 155 }}>KDelivery</th>
                                    </>
                                    : this.props.chainStatusId == ChainStatus.Broken || this.props.chainStatusId == ChainStatus.Ended ?
                                        <th style={{ width: 155 }}>Transplanted</th>
                                        : null}

                                {this.props.swapClusterStatusId == ClusterStatus.Transplanted && (
                                    <th style={{ width: 155 }}>
                                        <div style={{ paddingLeft: 10 }}>Transplanted</div>
                                    </th>
                                )}

                                {this.props.swapClusterStatusId == ClusterStatus.Offers || this.props.swapClusterStatusId == ClusterStatus.CrossMatching ?
                                    <>
                                        <th style={{ width: 155 }}>KPickup</th>
                                        <th style={{ width: 155 }}>KDelivery</th>
                                        <th style={{ width: 155 }}>
                                            {this.props.swapClusterStatusId == ClusterStatus.Offers ?
                                                <>Offer Deadline</>
                                                :
                                                <>DC Deadline</>
                                            }
                                        </th>
                                        {(this.props.swapClusterStatusId == ClusterStatus.Offers || this.state.queryStatus == ClusterStatus.KflId) && (
                                            <th style={{ width: 155 }}>Status</th>
                                        )}
                                        {this.props.swapClusterStatusId == ClusterStatus.CrossMatching && (
                                            <th style={{ width: 155, textAlign: "center" }}>Logistics</th>
                                        )}
                                    </>
                                    :
                                    <>
                                        <th style={{ width: 155, textAlign: "center" }}>Logistics</th>
                                    </>
                                }

                                {this.props.swapClusterStatusId == ClusterStatus.Kickoff && (
                                    <th style={{ width: 155, textAlign: "center" }}>Unos/Surg</th>
                                )}

                                <th></th>
                            </HeaderRowWrapper>
                        </thead>
                        <tbody>
                            {positions.map((position, index) => {
                                let placeholder = this.state.placeholders.find(x => x.donorId == position.donor.id);
                                let placeholderCenterId = placeholder ? placeholder.centerId : 0;


                                return (
                                    <PositionListRow
                                        key={`tr-${position.chainId}-${position.clusterNumber}-${position.positionNumber}`}
                                        index={index}
                                        position={position}
                                        chainStatusId={this.props.chainStatusId}
                                        clusterStatusId={this.props.clusterStatusId}
                                        swapChainStatusId={this.props.swapChainStatusId}
                                        swapClusterStatusId={this.props.swapClusterStatusId}
                                        pairStatusList={this.props.swaps.pairStatusList}
                                        centerList={this.props.swaps.centerList}
                                        distanceBetweenCentersList={this.props.swaps.distanceBetweenCenters}
                                        placeholder={placeholderCenterId}
                                        queryStatus={this.state.queryStatus}
                                        onShowMap={this.handleShowMap}
                                        onShowOverlay={this.handleShowOverlay}
                                        onHideOverlay={this.handleHideOverlay}
                                        onEditLogistics={this.handleEditLogistics}
                                        onResetChain={this.handleResetChain}
                                        onPairStatusChange={this.handlePairStatusChange}
                                        onKidneyPickupChange={this.handleKidneyPickupChange}
                                        onKidneyDeliveryChange={this.handleKidneyDeliveryChange}
                                        onCreatePlaceholder={this.handleCreatePlaceholder}
                                        onMatchOfferDeadlineChange={this.handleMatchOfferDeadlineChange}
                                        onDonorClearanceDeadlineChange={this.handleCrossMatchDeadlineChange}
                                        onTriggerRexm={this.handleConfirmRexm}
                                        onUpdateGps={this.handleUpdateGps}
                                        onCustomLogistics={this.handleEditCustomLogistics}
                                        onGetMatchingSurgeries={this.getMatchingSurgeries}
                                        onTogglePlaceholder={this.handleTogglePlaceholder}
                                    />
                                );
                            })}
                        </tbody>
                    </Table>
                </div>

                <RecipientOverlay
                    show={this.state.overlay.show && this.state.overlay.rowRef != null}
                    rowRef={this.state.overlay.rowRef}
                    tableRef={this.tableRef}
                    donor={this.state.overlay.donor}
                    intendedDonors={this.state.overlay.intendedDonors}
                    recipient={this.state.overlay.recipient}
                    donors={this.state.overlay.donors}
                    isMatchingPage={false} />

                <Logistics
                    show={this.state.logistics.show}
                    clusterId={this.props.clusterId}
                    data={this.state.logistics.data}
                    logisticsProviders={this.props.swaps.logisticsProviders}
                    distanceBetweenCentersList={this.props.swaps.distanceBetweenCenters}
                    transportTypes={this.props.swaps.transportTypeList}
                    logisticsList={this.state.logistics.list}
                    forCourier={false}
                    onSwitchToLogisticsPlan={this.handleSwitchToCustomLogistics}
                    onSave={this.handleSaveLogistics}
                    onCancel={this.handleCancelLogistics}
                    onAddressesExport={this.handleAddressDownload}
                    onContactsExport={this.handleContactsDownload}
                />

                <LogisticsOption
                    show={this.state.plans.show}
                    data={this.state.plans.data}
                    logisticsProviders={this.props.swaps.logisticsProviders}
                    transportTypes={this.props.swaps.transportTypeList}
                    audience={LogisticsAudience.NKR}
                    user={this.props.user}
                    onSwitchToSwapLogistics={this.handleSwitchToSwapLogistics}
                    onSave={this.handleSaveOptions}
                    onDelete={this.handleDeleteOptions}
                    onReturn={this.handleReturnOptions}
                    onCancel={this.handleCancelOptions}
                />

                <ResetConfirmation
                    show={this.state.reset.showConfirmation}
                    onResetYes={this.handleResetYes}
                    onResetNo={this.handleResetNo}
                />

                <TransplantDetails
                    show={this.state.transplantDetails.showTransplantDetails}
                    transplantDetails={this.state.transplantDetails.data}
                    logisticsProviders={this.props.swaps.logisticsProviders}
                    swapTypes={this.props.swaps.swapTypeList}
                    transportTypes={this.props.swaps.transportTypeList}
                    onSave={this.handleUpdateTransplantDetailsSave}
                    onCancel={this.handleUpdateTransplantDetailsCancel}
                />

                <DonorStatus
                    show={this.state.pairStatusDetails.show}
                    donorStatus={this.state.pairStatusDetails.donorStatus}
                    pairStatusList={this.props.swaps.pairStatusList}
                    onDonorStatusChange={this.handleDonorStatusChange}
                    onSave={this.handleUpdateDonorStatusSave}
                    onCancel={this.handleUpdateDonorStatusCancel}
                />

                <OverrideAnatomyPreference
                    show={this.state.overrideAnatomyPreference.show}
                    centerAlias={this.state.overrideAnatomyPreference.centerAlias}
                    onYes={this.handleOverrideAnatomyPreferenceYes}
                    onNo={this.handleOverrideAnatomyPreferenceNo}
                />

                <TriggerRexmEmail
                    show={this.state.triggerRexm.show}
                    onSend={this.handleTriggerRexmSend}
                    onCancel={this.handleTriggerRexmCancel}
                />

                <PairStatusChangeBlocked
                    show={this.state.markTransplantedBlocked.show}
                    blockedPosition={blockedPosition}
                    onClose={this.handleMarkTransplantedBlockedClose}
                />

                <div style={{ visibility: this.state.showPlaceholder ? 'visible' : 'hidden', position: 'fixed', top: 0, bottom: 0, left: 0, right: 0, background: 'rgba(192, 193, 193, 0.5)' }}></div>
            </>
        );
    }
}

const mapStateToProps = (state: IApplicationState): IStateProps => {
    return {
        swaps: state.swaps
    };
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getLogisticsData: (donorId, recipientId) => {
            return dispatch(SwapActionCreators.getLogisticsDataByDonorRecipient(donorId, recipientId));
        },
        getLogisticsDataByChainCluster: (chainId, clusterNumber) => {
            return dispatch(SwapActionCreators.getLogisticsDataByChainCluster(chainId, clusterNumber));
        },
        getLogisticsDataByChainClusterPosition: (chainId, clusterNumber, positionNumber) => {
            return dispatch(SwapActionCreators.getLogisticsDataByChainClusterPosition(chainId, clusterNumber, positionNumber));
        },
        saveLogisticsData: (logistics) => {
            return dispatch(SwapActionCreators.saveLogisticsData(logistics));
        },
        resetChain: (recipientId) => {
            return dispatch(SwapActionCreators.resetChain(recipientId));
        },
        savePairStatus: (request) => {
            return dispatch(SwapActionCreators.savePairStatus(request));
        },
        saveKidneyPickupDateTime: (chainId, clusterNumber, positionNumber, pickupDateTime, isLoop) => {
            return dispatch(SwapActionCreators.savePositionDateTime(SwapActionTypes.UPDATE_KIDNEYPICKUP_DATETIME, chainId, clusterNumber, positionNumber, pickupDateTime, isLoop));
        },
        saveKidneyArrivalDateTime: (chainId, clusterNumber, positionNumber, arrivalDateTime, isLoop) => {
            return dispatch(SwapActionCreators.savePositionDateTime(SwapActionTypes.UPDATE_KIDNEYARRIVAL_DATETIME, chainId, clusterNumber, positionNumber, arrivalDateTime, isLoop));
        },
        saveMatchOfferDeadlineDateTime: (chainId, clusterNumber, positionNumber, matchOfferDeadlineDateTime, isLoop) => {
            return dispatch(SwapActionCreators.savePositionDateTime(SwapActionTypes.UPDATE_MATCHOFFER_DEADLINE_DATETIME, chainId, clusterNumber, positionNumber, matchOfferDeadlineDateTime, isLoop));
        },
        saveDonorClearanceDeadlineDateTime: (chainId, clusterNumber, positionNumber, crossMatchDeadlineDateTime, isLoop) => {
            return dispatch(SwapActionCreators.savePositionDateTime(SwapActionTypes.UPDATE_CROSSMATCH_DEADLINE_DATETIME, chainId, clusterNumber, positionNumber, crossMatchDeadlineDateTime, isLoop));
        },
        createPlaceholder: (centerId, donorId) => {
            return dispatch(SwapActionCreators.createPlaceholder(centerId, donorId));
        },
        updateRexm: (donorId, recipientId) => {
            return dispatch(SwapActionCreators.updateRexm(donorId, recipientId));
        },
        updateGps: (clusterId, positionNumber, toggle) => {
            return dispatch(SwapActionCreators.toggleGps(clusterId, positionNumber, toggle));
        },
        getSwaps: (chainStatus, clusterStatus) => {
            return dispatch(SwapActionCreators.getSwaps(chainStatus, clusterStatus, true));
        },
        getLogisticsOption: (chainId, clusterNumber, positionNumber) => {
            return dispatch(SwapActionCreators.getLogisticsOption(chainId, clusterNumber, positionNumber));
        },
        saveLogisticsOption: (request) => {
            return dispatch(SwapActionCreators.saveLogisticsOption(request, true));
        },
        getMatchingSurgeriesAndRedeye: (dateValue, centerId, excludeId, isRecipient, chainId, clusterNumber, positionNumber) => {
            return dispatch(SwapActionCreators.getMatchingSurgeriesAndRedeye(dateValue, centerId, excludeId, isRecipient, chainId, clusterNumber, positionNumber));
        },
        getCenterAddressesDownload: (donorCenterId, recipCenterId, clusterId) => {
            return dispatch(CenterActionCreators.getCenterAddressesDownload(donorCenterId, recipCenterId, clusterId));
        },
        getCenterContactsDownload: (clusterId) => {
            return dispatch(ContactActionCreators.getContactResultsForLogistics(clusterId));
        },
        deleteLogisticsOptions: (chainId, clusterNumber, positionNumber) => {
            return dispatch(SwapActionCreators.deleteLogisticsOptions(chainId, clusterNumber, positionNumber));
        },
        returnLogisticsOptions: (chainId, clusterNumber, positionNumber) => {
            return dispatch(SwapActionCreators.returnLogisticsOptions(chainId, clusterNumber, positionNumber));
        }
    };
}

export default connect<IStateProps, IDispatchProps, IOwnProps>(
    mapStateToProps,
    mapDispatchToProps)(PositionList);